/**
 * ErrorBoundary — wrap any subtree to catch render errors without taking
 * down the whole page.
 *
 * Why this exists
 * ───────────────
 * React errors during render bubble all the way to the root by default. A
 * single bug in one chart's render path crashes the entire view, even if the
 * other sections are fine. Wrapping each major section in an ErrorBoundary
 * confines the blast radius to that section.
 *
 * Where to use it
 * ───────────────
 *   ✓ Around each Section / Card / Chart inside a page
 *   ✓ Around each top-level route component
 *   ✓ Around third-party components (charts, calendars, etc.) that we don't
 *     fully trust to handle malformed data
 *
 * What it does NOT catch
 * ──────────────────────
 * Per React docs, error boundaries do NOT catch:
 *   • errors inside event handlers — wrap those in try/catch yourself
 *   • async errors (promises, setTimeout) — same
 *   • errors during server-side rendering
 *   • errors thrown in the error boundary itself
 *
 * Usage
 * ─────
 *   <ErrorBoundary name="Price schedule chart">
 *     <LineChart … />
 *   </ErrorBoundary>
 *
 * The `name` prop is shown in the fallback so you know which section failed.
 *
 * Custom fallback
 * ───────────────
 * Pass `fallback={(error, reset) => …}` for a custom render. Default is a
 * compact "⚠ Section failed to load" card with retry + collapsible details.
 */

import React from 'react';
import { Box, Typography, Button, Collapse, Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, showDetails: false };
  }

  // Static so we can update state from a thrown error during render.
  static getDerivedStateFromError(error) {
    return { error };
  }

  // Called *after* an error in a descendant — good place to log to a service
  // (Sentry, Datadog, etc.). For now console.error is enough.
  componentDidCatch(error, info) {
    // Track which boundary caught it so logs are useful.
    const name = this.props.name || 'unnamed';
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${name}]`, error, info?.componentStack);
    this.setState({ info });
  }

  reset = () => this.setState({ error: null, info: null, showDetails: false });

  toggleDetails = () => this.setState((s) => ({ showDetails: !s.showDetails }));

  render() {
    const { error, info, showDetails } = this.state;
    const { children, name = 'this section', fallback, compact = false } = this.props;

    if (!error) return children;

    // Custom fallback render — full responsibility on caller.
    if (typeof fallback === 'function') {
      return fallback(error, this.reset);
    }

    // Default compact fallback — drops into the same slot as the original
    // section, so layout doesn't reflow much.
    return (
      <Box
        sx={{
          my: compact ? 1 : 2,
          p: compact ? 1.5 : 2,
          borderRadius: 1,
          border: '1px dashed',
          borderColor: 'error.main',
          bgcolor: 'rgba(229, 56, 79, 0.04)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: 'error.main' }}>
            {name} failed to render
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 1 }}>
          The rest of the page is still working — only this section crashed.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
            onClick={this.reset}
            sx={{ fontSize: '0.75rem', textTransform: 'none', color: 'error.main' }}
          >
            Try again
          </Button>
          <Button
            size="small"
            onClick={this.toggleDetails}
            sx={{ fontSize: '0.75rem', textTransform: 'none', color: 'text.secondary' }}
          >
            {showDetails ? 'Hide' : 'Show'} details
          </Button>
        </Box>

        <Collapse in={showDetails}>
          <Alert
            severity="error"
            variant="outlined"
            sx={{ mt: 1, fontSize: '0.72rem', '& .MuiAlert-message': { fontFamily: "'JetBrains Mono', monospace" } }}
          >
            <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {String(error?.message || error)}
              {info?.componentStack ? `\n${info.componentStack.trim()}` : ''}
            </Box>
          </Alert>
        </Collapse>
      </Box>
    );
  }
}

export default ErrorBoundary;
